import React, { useState, useEffect, useCallback } from 'react';
import { getActivityFeed } from '../api/apiClient';
import './AdminSettings.css';
import './AdminAnalytics.css';

const ACTION_META = {
  login:          { label: 'Login',         emoji: '🔑' },
  signup:         { label: 'Signup',        emoji: '✨' },
  announcement:   { label: 'Announcement',  emoji: '📢' },
  comment:        { label: 'Comment',       emoji: '💬' },
  admin_change:   { label: 'Admin Change',  emoji: '🛡️' },
  staff_removed:  { label: 'Staff Removed', emoji: '❌' },
  presence:       { label: 'Presence',      emoji: '📍' },
  poll_vote:      { label: 'Poll Vote',     emoji: '🗳️' },
  calendar_event: { label: 'Calendar',      emoji: '📅' },
};

const LIMIT = 25;

const relativeTime = (iso) => {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const AdminActivityLog = ({ currentUser }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchFeed = useCallback((pageNum, filter, replace) => {
    const setBusy = replace ? setLoading : setLoadingMore;
    setBusy(true);
    const params = { page: pageNum, limit: LIMIT };
    if (filter) params.action = filter;
    getActivityFeed(currentUser.id, params)
      .then(res => {
        setRows(prev => replace ? res.data.rows : [...prev, ...res.data.rows]);
        setTotal(res.data.total);
      })
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [currentUser.id]);

  useEffect(() => {
    setPage(1);
    fetchFeed(1, actionFilter, true);
  }, [actionFilter, fetchFeed]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchFeed(next, actionFilter, false);
  };

  const hasMore = rows.length < total;

  return (
    <div>
      <div className="ana-filter-bar">
        <select
          className="ana-filter-select"
          value={actionFilter}
          onChange={e => setActionFilter(e.target.value)}
        >
          <option value="">All Activity</option>
          {Object.entries(ACTION_META).map(([key, { label, emoji }]) => (
            <option key={key} value={key}>{emoji} {label}</option>
          ))}
        </select>
        <span className="adm-count">{total} event{total !== 1 ? 's' : ''}</span>
      </div>

      <div className="adm-body">
        {loading ? (
          <p className="adm-loading">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="adm-loading">No activity recorded yet.</p>
        ) : (
          <ul className="ana-feed-list">
            {rows.map(row => {
              const meta = ACTION_META[row.action] || { label: row.action, emoji: '•' };
              const name = row.staff?.name || 'A former staff member';
              const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
              return (
                <li key={row.id} className="ana-feed-row">
                  <div className="adm-avatar">
                    {row.staff?.avatar
                      ? <img src={row.staff.avatar} alt={name} />
                      : <span>{initials}</span>}
                  </div>
                  <span className="ana-feed-action" title={meta.label}>{meta.emoji}</span>
                  <div className="ana-feed-info">
                    <span className="ana-feed-desc"><span className="ana-feed-name">{name}</span> {row.description}</span>
                  </div>
                  <span className="ana-feed-time">{relativeTime(row.created_at)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {!loading && hasMore && (
        <div className="ana-load-more-wrap">
          <button className="adm-btn" onClick={handleLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminActivityLog;
